// GA4 recommended events, user identity and once-only conversion tracking.
// Every function here is fire-and-forget and never throws, so tracking can
// never break a signup or a checkout.
import { IAccount, IActiveSubscription } from "~/types/account";
import { IPlan } from "~/types/billing";

// Wide enough to absorb a client clock that is off by a few minutes; a
// sign-up is still sent once per account id (see trackSignUp).
const SIGNUP_WINDOW_MS = 60 * 60 * 1000;
const TRIAL_START_WINDOW_MS = 60 * 60 * 1000;
const TRACKED_KEY_PREFIX = "conversion_tracked_";
// The $1.99 first-trial fee, used when Stripe did not return the amount.
export const TRIAL_FEE = 1.99;

// What the checkout knows once Stripe has confirmed a payment.
export interface PaymentTracking {
  // Stripe PaymentIntent or SetupIntent id; one payment is tracked once.
  intentId?: string;
  // Charged amount in major units, when Stripe returned it.
  value?: number;
  currency?: string;
  // The subscription the API confirmed after the payment, when known.
  subscription?: IActiveSubscription | null;
}

// GTM loads after first paint and replays whatever was queued before it.
const pushToDataLayer = (data: Record<string, unknown>) => {
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push(data);
};

// gtag() commands must reach the dataLayer as an arguments object.
function gtag(..._args: unknown[]) {
  // eslint-disable-next-line prefer-rest-params
  pushToDataLayer(arguments as unknown as Record<string, unknown>);
}

// Runs one tracking call; a failure never stops the calls after it.
export const safely = (track: () => void) => {
  try {
    track();
  } catch {
    // tracking is optional
  }
};

const storageKey = (key: string) => `${TRACKED_KEY_PREFIX}${key}`;

const wasTracked = (key: string, storage: () => Storage) => {
  try {
    return !!storage().getItem(storageKey(key));
  } catch {
    // storage blocked: track rather than lose the conversion
    return false;
  }
};

const markTracked = (key: string, storage: () => Storage) => {
  try {
    storage().setItem(storageKey(key), "1");
  } catch {
    // storage blocked
  }
};

// Runs track() once per key: a Stripe return page that re-renders, or a
// reload of it, must not count the same payment again. The key is marked
// after track() has run. Without a key there is nothing to dedupe on, so
// track() runs as before. Errors are swallowed.
export const trackOnce = (
  key: string | undefined,
  track: () => void,
  storage: () => Storage = () => window.sessionStorage
) => {
  if (key && wasTracked(key, storage)) return;
  try {
    track();
  } catch {
    // tracking is optional
  } finally {
    if (key) markTracked(key, storage);
  }
};

// A first trial started in this checkout:
// - Stripe confirmed a charge (a PaymentIntent with an amount). The API
//   charges the trial fee only on an account's first trial, and a plan
//   change during a trial is not charged, so neither of those counts.
// - The API confirms the subscription for this plan is in a trial that was
//   created in the last hour.
// A Shopify charge or a payment without a Stripe intent never counts.
export const isConfirmedTrialStart = (
  tracking: PaymentTracking | undefined,
  plan: IPlan | undefined
) => {
  try {
    const { intentId, value, subscription } = tracking ?? {};
    if (!intentId?.startsWith("pi_") || !value || value <= 0) return false;
    if (!subscription) return false;
    if (plan?.id && subscription.plan?.id && subscription.plan.id !== plan.id)
      return false;
    const now = Date.now();
    return (
      Date.parse(subscription.trial_end_at) > now &&
      now - Date.parse(subscription.created_at) < TRIAL_START_WINDOW_MS
    );
  } catch {
    return false;
  }
};

// The trial charge: Stripe's amount when known, otherwise the trial fee.
export const trialValue = (tracking: PaymentTracking | undefined) =>
  typeof tracking?.value === "number" ? tracking.value : TRIAL_FEE;

export const triggerTrialStartedEvent = (
  tracking: PaymentTracking | undefined,
  plan: IPlan
) => {
  try {
    const event: Record<string, unknown> = {
      event: "trial_started",
      value: trialValue(tracking),
      currency: (tracking?.currency ?? "USD").toUpperCase(),
      plan: `${plan.name.toLowerCase()}-${plan.interval}`,
    };
    if (tracking?.intentId) event.transaction_id = tracking.intentId;
    pushToDataLayer(event);
  } catch {
    // tracking is optional
  }
};

export const triggerSignUpEvent = (method: string) => {
  try {
    pushToDataLayer({ event: "sign_up", method });
  } catch {
    // tracking is optional
  }
};

// GA4 user_id plus the email for enhanced conversions (gtag normalises and
// hashes it before sending).
// The account is re-fetched often (every page that mounts useAccount, the
// post-payment poll), so the user is only identified again when it changes.
let lastIdentified: string | null = null;

export const identifyAnalyticsUser = (account: IAccount) => {
  try {
    if (!account?.id) return;
    const userId = String(account.id);
    const identity = `${userId}|${account.email ?? ""}`;
    if (identity === lastIdentified) return;
    gtag("set", { user_id: userId });
    pushToDataLayer({ user_id: userId });
    if (account.email) gtag("set", "user_data", { email: account.email });
    lastIdentified = identity;
  } catch {
    // tracking is optional
  }
};

// A social login answers the same way for a new and an existing account; the
// account's creation time tells them apart.
export const isNewAccount = (account: IAccount | null | undefined) => {
  try {
    const created = Date.parse(account?.created_at ?? "");
    return (
      !Number.isNaN(created) &&
      Math.abs(Date.now() - created) < SIGNUP_WINDOW_MS
    );
  } catch {
    return false;
  }
};

// sign_up once per account id, whichever way the account signs up or logs in
// first (email signup, then social login, must not count twice).
export const trackSignUp = (
  accountId: number | string | undefined,
  method: string
) =>
  trackOnce(
    accountId ? `signup_${accountId}` : undefined,
    () => triggerSignUpEvent(method),
    () => window.localStorage
  );

// A social login answers the same way for a new and an existing account (the
// API has no new-account flag), so the account's creation time is the only
// signal that this login created it.
export const trackSocialSignup = (
  account: IAccount | null | undefined,
  method: string
) => {
  if (!account?.id || !isNewAccount(account)) return;
  trackSignUp(account.id, method);
};
